import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Gamepad2, Trophy, Clock, Zap, ArrowRight } from 'lucide-react'
import { useApp } from '../contexts/AppContext'
import Button from '../components/ui/Button'
import Card, { CardHeader, CardContent, CardFooter } from '../components/ui/Card'

export default function HomePage() {
  const { getRecentResults } = useApp()
  
  const ticTacToeResults = getRecentResults('tic-tac-toe', 3)
  const game2048Results = getRecentResults('2048', 3)
  
  const games = [
    {
      id: 'tic-tac-toe',
      title: 'Tic Tac Toe',
      description: 'Classic 3x3 grid game. Play against the computer or a friend.',
      icon: Gamepad2,
      color: 'primary',
      path: '/tic-tac-toe',
      features: ['2 Players', 'Simple Rules', 'Quick Games']
    },
    {
      id: '2048',
      title: '2048',
      description: 'Slide numbered tiles to combine them and reach the 2048 tile.',
      icon: Zap,
      color: 'secondary',
      path: '/2048',
      features: ['Single Player', 'Strategy Game', 'Addictive']
    }
  ]
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  }
  
  return (
    <div className="max-w-7xl mx-auto">
      {/* Hero Section */}
      <motion.div 
        className="text-center mb-12"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
          Basic Website Games
        </h1>
        <p className="text-xl text-text-muted mb-8 max-w-3xl mx-auto">
          A collection of classic games built with React 19, TypeScript, and modern web technologies.
          Play, compete, and track your progress.
        </p>
      </motion.div>
      
      {/* Games Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {games.map((game) => (
          <motion.div key={game.id} variants={itemVariants}>
            <Card shadow="lg" padding="lg" className="h-full">
              <CardHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-xl bg-${game.color} bg-opacity-10`}>
                    <game.icon className={`w-8 h-8 text-${game.color}`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{game.title}</h2>
                    <p className="text-text-muted">{game.description}</p>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <div className="mb-6">
                  <h3 className="font-semibold mb-2">Features</h3>
                  <div className="flex flex-wrap gap-2">
                    {game.features.map((feature, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 rounded-full bg-surface-variant text-sm"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold mb-1">Recent Games</h3>
                    <div className="text-sm text-text-muted">
                      {game.id === 'tic-tac-toe' ? (
                        ticTacToeResults.length > 0 ? (
                          <div>
                            {ticTacToeResults.map((result, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Trophy className="w-3 h-3" />
                                <span>
                                  {result.winner ? `${result.winner} won` : 'Draw'} - Score: {result.score}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p>No games played yet</p>
                        )
                      ) : (
                        game2048Results.length > 0 ? (
                          <div>
                            {game2048Results.map((result, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <Trophy className="w-3 h-3" />
                                <span>Score: {result.score} - Moves: {result.moves}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p>No games played yet</p>
                        )
                      )}
                    </div>
                  </div>
                  
                  <Button
                    as={Link}
                    to={game.path}
                    variant="primary"
                    size="lg"
                    className="group"
                    icon={ArrowRight}
                    iconPosition="right"
                    iconClassName="group-hover:translate-x-1 transition-transform"
                  >
                    Play Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
      
      {/* Stats Section */}
      <motion.div 
        className="mb-12"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card shadow="md">
          <CardHeader>
            <h2 className="text-2xl font-bold">Game Statistics</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-xl bg-surface-variant">
                <div className="flex justify-center mb-4">
                  <Trophy className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Total Games Played</h3>
                <p className="text-3xl font-bold">
                  {ticTacToeResults.length + game2048Results.length}
                </p>
              </div>
              
              <div className="text-center p-6 rounded-xl bg-surface-variant">
                <div className="flex justify-center mb-4">
                  <Clock className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Recent Activity</h3>
                <p className="text-3xl font-bold">
                  {ticTacToeResults.length > 0 || game2048Results.length > 0 ? 'Active' : 'None'}
                </p>
              </div>
              
              <div className="text-center p-6 rounded-xl bg-surface-variant">
                <div className="flex justify-center mb-4">
                  <Zap className="w-8 h-8 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Games Available</h3>
                <p className="text-3xl font-bold">2</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
      
      {/* Instructions */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <Card>
          <CardHeader>
            <h2 className="text-2xl font-bold">How to Play</h2>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-2">Tic Tac Toe</h3>
                <ul className="space-y-2 text-text-muted">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>Players take turns placing X and O marks on a 3x3 grid</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>The first player to get 3 of their marks in a row wins</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>If all 9 squares are filled without a winner, it's a draw</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2">2048</h3>
                <ul className="space-y-2 text-text-muted">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    <span>Use arrow keys to slide tiles in four directions</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    <span>When two tiles with the same number touch, they merge into one</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary">•</span>
                    <span>Try to create a tile with the number 2048 to win</span>
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
          <CardFooter className="border-t border-border pt-6">
            <p className="text-text-muted text-center w-full">
              All game results are saved locally in your browser. Your progress will be preserved between sessions.
            </p>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  )
}